import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Modal, Select } from 'antd';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { SlugInput } from '@shared/components/SlugInput/SlugInput';
import { pickDefaultLanguage, useActiveLanguages } from '@features/languages/useLanguages';
import { FORM_TYPES, type FormTypeValue } from '../constants';
import type { FormCreateInput } from '../types';

interface FormValues {
  title: string;
  formType: FormTypeValue;
  slug: string;
  languageId?: string;
}

interface FormCreateModalProps {
  open: boolean;
  saving: boolean;
  onCancel: () => void;
  onCreate: (input: FormCreateInput) => void;
}

/** "Yeni Form" — başlık/tip/slug/dil kabuğu oluşturur; alanlar builder'da doldurulur. */
export function FormCreateModal({ open, saving, onCancel, onCreate }: FormCreateModalProps) {
  const { t } = useTranslation(['forms', 'common']);
  const td = t as unknown as (key: string) => string;
  const [form] = Form.useForm<FormValues>();
  const title = Form.useWatch('title', form);
  const { data: languages = [] } = useActiveLanguages();

  useEffect(() => {
    if (!open) return;
    // AntD form instance (React setState değil) — effect-setState kuralını tetiklemez.
    form.setFieldsValue({
      title: '',
      formType: 'Contact',
      slug: '',
      languageId: pickDefaultLanguage(languages)?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, languages]);

  const submit = () => {
    void (async () => {
      try {
        const v = await form.validateFields();
        onCreate({
          title: v.title.trim(),
          formType: v.formType,
          slug: v.slug?.trim() || null,
          languageId: v.languageId ?? null,
        });
      } catch {
        // inline validation
      }
    })();
  };

  return (
    <Modal
      open={open}
      title={t('create.title')}
      onCancel={onCancel}
      onOk={submit}
      okText={t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={saving}
      destroyOnClose
      maskClosable={!saving}
    >
      <Form<FormValues> form={form} layout="vertical" initialValues={{ formType: 'Contact' }}>
        <Form.Item
          name="title"
          label={t('create.titleField')}
          rules={[{ required: true, message: t('create.titleRequired') }]}
        >
          <Input maxLength={200} placeholder={t('create.titlePlaceholder')} data-testid="form-create-title" />
        </Form.Item>

        <Form.Item name="formType" label={t('create.typeField')} help={t('create.typeHint')}>
          <Select<FormTypeValue>
            options={FORM_TYPES.map((type) => ({ value: type, label: td(`type.${type}`) }))}
            data-testid="form-create-type"
          />
        </Form.Item>

        <Form.Item name="slug" label={t('create.slugField')}>
          <SlugField sourceValue={title ?? ''} />
        </Form.Item>

        <Form.Item name="languageId" label={t('common:contentLanguage.label')} help={t('create.languageHint')}>
          <LanguageField />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function SlugField({
  value,
  onChange,
  sourceValue,
}: {
  value?: string;
  onChange?: (next: string) => void;
  sourceValue: string;
}) {
  return <SlugInput value={value ?? ''} sourceValue={sourceValue} onChange={(v) => onChange?.(v)} />;
}

function LanguageField({
  value,
  onChange,
}: {
  value?: string;
  onChange?: (next: string | undefined) => void;
}) {
  return <LanguagePicker value={value} onChange={(v) => onChange?.(v)} style={{ width: '100%' }} />;
}
